import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import "./PDInvoice.css";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export default function PDInvoiceIndia({subscription, taxMeta}) {
    const authState = useSelector((state) => state.authReducer);

    if (!subscription) return null;
    if (!taxMeta) return null;

    const safeNumber = (val) => {
        const n = Number(val);
        return isNaN(n) ? 0 : n;
    };
// console.log(subscription);

    const handlePrint = () => {
        window.print();
    };

    const handleDownload = async () => {
        const element = document.getElementById("invoice-box");

        if (!element) return;

        const canvas = await html2canvas(element, {
            scale: 2, // high quality
            useCORS: true,
            backgroundColor: "#ffffff"
        });

        const imgData = canvas.toDataURL("image/png");

        const pdf = new jsPDF("p", "mm", "a4");

        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        const imgWidth = pdfWidth;
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;

        // multi-page support
        while (heightLeft > 0) {
            position = heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
            heightLeft -= pdfHeight;
        }

        pdf.save(`invoice-${subscription.invoiceNumber || "bill"}.pdf`);
    };

    const handleShare = async () => {
        const product = subscription?.product || "-";
        const invoiceNo = subscription?.invoiceNumber || "-";
        const name = subscription?.userName || "";
        const amount = subscription?.finalAmount || 0;
        const currency = subscription?.currencySymbol || "";

        const message = `
    🧾 *Invoice Details*

    Invoice No: ${invoiceNo}
    Name: ${name}
    Amount: ${currency} ${Number(amount).toFixed(2)}
    Product: ${product}

    Thank you for your payment.
        `.trim();

        // WhatsApp deep link
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;

        try {
            // Try native share first (mobile apps)
            if (navigator.share) {
                await navigator.share({
                    title: "Invoice",
                    text: message,
                    url: window.location.href
                });
            } else {
                // fallback → WhatsApp web/app
                window.open(whatsappUrl, "_blank");
            }
        } catch (err) {
            console.log(err);
            window.open(whatsappUrl, "_blank");
        }
    };

    const handleEmail = () => {
        const subject = `Invoice ${subscription.invoiceNumber}`;
        const body = `
    Hi,

    Please find your invoice details:

    Invoice No: ${subscription.invoiceNumber}
    Name: ${subscription.userName}
    Amount: ${subscription?.finalAmount}

    Product: ${subscription?.product}

    Thank you.
    `;

        window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    };

    const durationDays = subscription.startDate && subscription.endDate
        ? Math.ceil(
            (new Date(subscription.endDate) - new Date(subscription.startDate)) /
            (1000 * 60 * 60 * 24)
        )
        : null;

    const isMaharashtraState = (state) => {
        const normalized = (state || "").trim().toLowerCase();
        return ["maharashtra", "mh", "maha"].includes(normalized);
    };

    const getGSTLabel = (state) => {
        const normalized = (state || "").trim().toLowerCase();

        return ["maharashtra", "mh", "maha"].includes(normalized)
            ? (
            <>
                <div>CGST (9%)</div>
                <div>SGST (9%)</div>
            </>
            )
            : (
            <div>IGST (18%)</div>
            );
    };

    return (
        <div className="mt-4 border border-purple-700 rounded-xl overflow-hidden bg-[#111827]">
            <div className="bg-purple-900/30 border-b border-purple-700 p-4 flex justify-between items-center">
                <div className="grid grid-cols-4 gap-2 text-gray-200 text-xs">
                    <button onClick={() => handleDownload()} title="Download Invoice">
                        ⬇️
                    </button>
                    <button onClick={() => handlePrint()} title="Print Invoice">
                        🖨️
                    </button>
                    <button onClick={() => handleShare()} title="Share Invoice">
                        🔗
                    </button>
                    <button onClick={() => handleEmail()} title="Email Invoice">
                        ✉️
                    </button>
                </div>
            </div>
            <div id="invoice-box" className="mt-4 p-4 border border-purple-700 rounded-xl overflow-hidden bg-[#111827]">
                <div>
                    <div className="text-xs text-gray-300">
                        TAX INVOICE (India)
                    </div>
                </div>
                <div className="border-t border-gray-600 pt-4">
                    <div className="text-sm font-semibold text-cyan-400 mb-2">
                        Supplier Details
                    </div>
                    <div className="text-xs text-gray-100">
                        {taxMeta.companyName}
                    </div>
                    <div className="text-xs text-gray-100">
                        {taxMeta.companyAddress}
                    </div>
                    <div className="text-xs text-gray-100">
                        CIN:{" "}{taxMeta.CINNo || "-"}
                    </div>

                    <div className="text-xs text-gray-100">
                        GSTN:{" "}{taxMeta.GSTNo || "-"}
                    </div>
                </div>
                <div className="p-4 space-y-4">
                    <div className="border-t border-gray-600 pt-4">
                         <div className="text-sm font-semibold text-cyan-400 mb-2">
                            Invoice Details
                        </div>
                        <div className="text-gray-100 text-xs">
                            Invoice No: 
                            {subscription.invoiceNumber || "-"}
                        </div>
                    
                        <div className="text-gray-100 text-xs">
                            Invoice Date:
                            {subscription.paymentDate ? new Date(subscription.paymentDate).toLocaleDateString(): "-"}
                        </div>                            
                    </div>
                    <div className="border-t border-gray-600 pt-4">
                        <div className="text-sm font-semibold text-cyan-400 mb-2">
                            Customer Details
                        </div>
                        <div className="text-xs text-gray-100">
                            Name: {subscription.userName}
                        </div>
                        <div className="text-xs text-gray-100">
                            User Code: {subscription.userCode}
                        </div>
                        <div className="text-xs text-gray-100">
                            Sales Location: 
                            {subscription.state}{", "}
                            {subscription.country}{", "}
                        </div>
                        <div className="text-xs text-gray-100">
                            User Account Location: 
                            {authState.user.area}{", "}
                            {authState.user.city}{", "}
                            {authState.user.state}{", "}
                            {authState.user.country}{", "}
                            {authState.user.pincode}
                        </div>
                    </div>
                    <div className="border-t border-gray-600 pt-4">
                        <div className="text-sm font-semibold text-cyan-400 mb-2">
                            Service Description
                        </div>
                        <div className="text-xs text-gray-100">
                            {subscription.product} Premium Subscription as OIDAR (Online Information and Database Access or Retrieval) services
                        </div>
                        {/* <div className="text-xs text-gray-100">
                            Access Duration: 
                            {durationDays ? `${durationDays} Days` : "-"}{" "}
                            Starting From: {" "}{subscription.startDate ? new Date(subscription.startDate).toLocaleDateString(): "-"}
                        </div> */}
                        <div className="text-xs text-gray-100">
                            Subscription ID: {subscription.subscriptionId || "-"}
                        </div>
                        <div className="text-xs text-gray-100">
                            SAC: 998439
                            {/* {" "}{taxMeta.HSNCode} */}
                        </div>
                    </div>
                    <div className="border-t border-gray-600 pt-2">
                        <div className="text-sm font-semibold text-cyan-400 mb-2">
                            Tax Summary
                        </div>
                        <div className="grid grid-cols-2 text-xs gap-y-1">
                            <div className="text-gray-100">
                                Gross Amount
                            </div>
                            <div className="text-right text-white">
                                {subscription.currencySymbol}{" "}{(Number(subscription?.finalAmount || 0) - Number(subscription?.tax || 0)).toFixed(2)}
                            </div>
                            {isMaharashtraState(subscription.state) ? (
                                <>
                                    <div className="text-gray-100">CGST (9%)</div>
                                    <div className="text-right text-white">
                                    {subscription.currencySymbol}{" "}
                                    {(Number(subscription?.tax || 0) / 2).toFixed(2)}
                                    </div>

                                    <div className="text-gray-100 whitespace-nowrap">Maharashtra SGST (9%)</div>
                                    <div className="text-right text-white">
                                    {subscription.currencySymbol}{" "}
                                    {(Number(subscription?.tax || 0) / 2).toFixed(2)}
                                    </div>
                                </>
                                ) : (
                                <>
                                    <div className="text-gray-100">IGST (18%)</div>
                                    <div className="text-right text-white">
                                    {subscription.currencySymbol}{" "}
                                    {Number(subscription?.tax || 0).toFixed(2)}
                                    </div>
                                </>
                            )}

                            <div className="text-gray-100">
                                Net Amount
                            </div>
                            <div className="text-right text-white">
                                {subscription.currencySymbol}{" "}{Number(subscription?.finalAmount || 0).toFixed(2)}
                            </div>
                            <div className="text-gray-100 font-bold">
                                Total Paid
                            </div>

                            <div className="text-right text-green-400 font-bold">
                                {subscription.currencySymbol}{" "}{Number(subscription?.finalAmount || 0).toFixed(2)}
                            </div>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 pt-4">
                        <div className="text-sm font-semibold text-cyan-400 mb-2">
                            Terms & Conditions
                        </div>
                        {(taxMeta.termsAndConditions || [])
                            .map((item, index) => (
                                <div
                                    key={index}
                                    className="text-[11px] text-gray-100"
                                >
                                    • {item}
                                </div>
                            ))}
                    </div>

                    <div className="border-t border-gray-800 pt-4 text-center">
                        <div className="text-[11px] text-gray-200">
                            This is a computer generated invoice.
                        </div>
                        <div className="text-[11px] text-gray-200">
                            No signature required.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}